import type {
    ApiSubmittalDetail,
    ApiSubmittalReview,
    ApiSubmittalReviewStatus,
} from "../../shared/interfaces";
import type { ProjectTransport } from "./transport";
import {
    failValidation,
    optionalField,
    requireArray,
    requireBoolean,
    requireNonNegativeInteger,
    requireNullableString,
    requireNullableWireString,
    requireOptionalNullableWireString,
    requireOptionalWireString,
    requireRecord,
    requireString,
    requireWireString,
    requireWireStringArray,
    validateResponseListRows,
} from "./validation";

const SUBMITTAL_REVIEWS_ENDPOINT = "/api/ai/workflow/submittal_review";
const REVIEW_STATUSES = ["pending", "in_progress", "completed", "failed"] as const;
const SUBMITTAL_TYPES = ["product_data", "shop_drawing", "test_report", "samples"] as const;

/** Server review wire shape. Null marks values that are not assigned or completed yet. */
export type ProjectSubmittalReview = Omit<
    ApiSubmittalReview,
    "completed_at" | "display_number" | "document_name" | "sequence_no"
> & {
    completed_at?: string | null;
    display_number?: string | null;
    sequence_no?: number | null;
    /** The serializer emits an empty string when the file has no name. */
    document_name?: string;
};

export type ProjectSubmittalReviewStatus = Omit<ApiSubmittalReviewStatus, "completed_at"> & {
    completed_at?: string | null;
};

function requireMemberOf(
    value: unknown,
    values: readonly string[],
    endpoint: string,
    field: string,
): string {
    const text = requireString(value, endpoint, field);
    if (!values.includes(text)) return failValidation(endpoint, field);
    return text;
}

function requireStatusMemberOf(
    value: unknown,
    values: readonly string[],
    endpoint: string,
    field: string,
): string {
    const text = requireWireString(value, endpoint, field);
    if (text !== "" && !values.includes(text)) return failValidation(endpoint, field);
    return text;
}

function validateSubmittalDetail(value: unknown, endpoint: string): ApiSubmittalDetail {
    const row = requireRecord(value, endpoint, "response");
    if (row.kind !== "submittal") return failValidation(endpoint, "kind");
    requireString(row.documentId, endpoint, "documentId");
    requireNullableWireString(row.title, endpoint, "title");
    requireWireString(row.source, endpoint, "source");
    requireNullableWireString(row.externalUrl, endpoint, "externalUrl");
    requireNullableWireString(row.status, endpoint, "status");
    requireNullableWireString(row.number, endpoint, "number");
    return row as unknown as ApiSubmittalDetail;
}

function validateSubmittalReview(
    value: unknown,
    endpoint: string,
    field: string,
): ProjectSubmittalReview {
    const row = requireRecord(value, endpoint, field);
    requireString(row.id, endpoint, `${field}.id`);
    requireString(row.user_id, endpoint, `${field}.user_id`);
    requireNullableString(row.submittal_file_id, endpoint, `${field}.submittal_file_id`);
    requireMemberOf(row.submittal_type, SUBMITTAL_TYPES, endpoint, `${field}.submittal_type`);
    requireStatusMemberOf(row.review_status, REVIEW_STATUSES, endpoint, `${field}.review_status`);
    requireArray(row.product_reviews, endpoint, `${field}.product_reviews`);
    requireWireString(row.created_at, endpoint, `${field}.created_at`);
    requireOptionalNullableWireString(
        optionalField(row, "completed_at"),
        endpoint,
        `${field}.completed_at`,
    );
    const sequenceNumber = optionalField(row, "sequence_no");
    if (sequenceNumber !== undefined && sequenceNumber !== null) {
        requireNonNegativeInteger(sequenceNumber, endpoint, `${field}.sequence_no`);
    }
    requireOptionalNullableWireString(
        optionalField(row, "display_number"),
        endpoint,
        `${field}.display_number`,
    );
    requireOptionalWireString(
        optionalField(row, "document_name"),
        endpoint,
        `${field}.document_name`,
    );
    requireOptionalNullableWireString(
        optionalField(row, "progress_stage"),
        endpoint,
        `${field}.progress_stage`,
    );
    const productNamesValue = optionalField(row, "product_names");
    if (productNamesValue !== undefined) {
        requireWireStringArray(productNamesValue, endpoint, `${field}.product_names`);
    }
    return row as unknown as ProjectSubmittalReview;
}

function validateSubmittalReviewStatus(
    value: unknown,
    endpoint: string,
): ProjectSubmittalReviewStatus {
    const row = requireRecord(value, endpoint, "response");
    requireString(row.id, endpoint, "id");
    requireStatusMemberOf(row.status, REVIEW_STATUSES, endpoint, "status");
    requireWireString(row.created_at, endpoint, "created_at");
    requireOptionalNullableWireString(optionalField(row, "completed_at"), endpoint, "completed_at");
    requireBoolean(row.has_results, endpoint, "has_results");
    requireOptionalNullableWireString(
        optionalField(row, "progress_stage"),
        endpoint,
        "progress_stage",
    );
    return row as unknown as ProjectSubmittalReviewStatus;
}

export interface ProjectSubmittalApi {
    getByDocumentId(documentId: string, signal?: AbortSignal): Promise<ApiSubmittalDetail | null>;
    getSubmittalReview(id: string, signal?: AbortSignal): Promise<ProjectSubmittalReview>;
    getSubmittalReviewStatus(
        id: string,
        signal?: AbortSignal,
    ): Promise<ProjectSubmittalReviewStatus>;
    listSubmittalReviews(signal?: AbortSignal): Promise<ProjectSubmittalReview[]>;
}

export function createSubmittalApi(transport: ProjectTransport): ProjectSubmittalApi {
    return {
        async getByDocumentId(documentId, signal) {
            const path = `/api/ai/document/${documentId}/submittal`;
            try {
                const response = await transport.request<unknown>({ method: "GET", path, signal });
                return validateSubmittalDetail(response, path);
            } catch (error) {
                if (transport.getErrorStatus(error) === 404) return null;
                throw error;
            }
        },

        async getSubmittalReview(id, signal) {
            const path = `${SUBMITTAL_REVIEWS_ENDPOINT}/${id}`;
            const response = await transport.request<unknown>({ method: "GET", path, signal });
            return validateSubmittalReview(response, path, "response");
        },

        async getSubmittalReviewStatus(id, signal) {
            const path = `${SUBMITTAL_REVIEWS_ENDPOINT}/${id}/status`;
            const response = await transport.request<unknown>({ method: "GET", path, signal });
            return validateSubmittalReviewStatus(response, path);
        },

        async listSubmittalReviews(signal) {
            const response = await transport.request<unknown>({
                method: "GET",
                path: SUBMITTAL_REVIEWS_ENDPOINT,
                signal,
            });
            const rows = requireArray(response, SUBMITTAL_REVIEWS_ENDPOINT, "response");
            return validateResponseListRows(
                rows,
                SUBMITTAL_REVIEWS_ENDPOINT,
                "submittal review",
                (value, field) => validateSubmittalReview(value, SUBMITTAL_REVIEWS_ENDPOINT, field),
            );
        },
    };
}
